/*
 * Where to draw a workflow graph: a layered, left-to-right layout.
 *
 * A specimen's stages form a small DAG (the server keeps it acyclic). Each
 * stage goes in the column of its longest path from a start, so every arrow
 * points right; within a column, stages are ordered by the average row of
 * what leads into them, which keeps a straight chain straight and a branch
 * beside its parent. Deterministic: the same graph always lands the same way.
 *
 * Pure, so the geometry is tested without rendering anything.
 */

export const NODE = { w: 190, h: 92 };
// Wide enough that an arrow's own buttons, at its middle, clear the "+"
// handle on the edge of the stage it leaves.
export const GAP = { x: 92, y: 28 };
export const PAD = 16;

export function layoutDag(nodes, edges, { node = NODE, gap = GAP, pad = PAD } = {}) {
  const ids = nodes.map((n) => n.id);
  const known = new Set(ids);
  const live = edges.filter((e) => known.has(e.from) && known.has(e.to) && e.from !== e.to);
  const incoming = new Map(ids.map((id) => [id, []]));
  const outgoing = new Map(ids.map((id) => [id, []]));
  live.forEach((e) => { incoming.get(e.to).push(e.from); outgoing.get(e.from).push(e.to); });

  // Columns: the longest path from a stage with nothing before it (Kahn's
  // order). Anything left over sits in a cycle the server should never have
  // allowed; it goes after everything else rather than being lost.
  const layer = new Map();
  const pending = new Map(ids.map((id) => [id, incoming.get(id).length]));
  const queue = ids.filter((id) => pending.get(id) === 0);
  queue.forEach((id) => layer.set(id, 0));
  for (let i = 0; i < queue.length; i += 1) {
    const id = queue[i];
    for (const next of outgoing.get(id)) {
      layer.set(next, Math.max(layer.get(next) ?? 0, layer.get(id) + 1));
      pending.set(next, pending.get(next) - 1);
      if (pending.get(next) === 0) queue.push(next);
    }
  }
  let deepest = Math.max(-1, ...layer.values());
  ids.forEach((id) => { if (!layer.has(id)) layer.set(id, (deepest += 1)); });

  // Rows: input order in the first column, then by the barycentre of each
  // stage's predecessors, ties broken by input order.
  const order = new Map(ids.map((id, i) => [id, i]));
  const columns = [];
  ids.forEach((id) => { (columns[layer.get(id)] ||= []).push(id); });
  const row = new Map();
  columns.forEach((column, c) => {
    if (c > 0) {
      column.sort((a, b) => {
        const centre = (id) => {
          const before = incoming.get(id).filter((p) => row.has(p));
          return before.length ? before.reduce((s, p) => s + row.get(p), 0) / before.length : Infinity;
        };
        return (centre(a) - centre(b)) || (order.get(a) - order.get(b));
      });
    }
    column.forEach((id, r) => row.set(id, r));
  });

  const tallest = Math.max(1, ...columns.map((c) => (c ? c.length : 0)));
  const positions = {};
  columns.forEach((column, c) => {
    if (!column) return;
    // Centre a short column against the tallest one.
    const offset = ((tallest - column.length) * (node.h + gap.y)) / 2;
    column.forEach((id, r) => {
      positions[id] = {
        x: pad + c * (node.w + gap.x),
        y: pad + offset + r * (node.h + gap.y),
        column: c,
        row: r,
      };
    });
  });

  const paths = live.map((e) => {
    const a = positions[e.from];
    const b = positions[e.to];
    const x1 = a.x + node.w;
    const y1 = a.y + node.h / 2;
    const x2 = b.x;
    const y2 = b.y + node.h / 2;
    const bend = Math.max(24, (x2 - x1) / 2);
    return {
      ...e,
      d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
      mid: { x: (x1 + x2) / 2, y: (y1 + y2) / 2 },
    };
  });

  const columnCount = columns.length || 1;
  return {
    positions,
    paths,
    width: pad * 2 + columnCount * node.w + (columnCount - 1) * gap.x,
    height: pad * 2 + tallest * node.h + (tallest - 1) * gap.y,
    node,
  };
}
